import React, { useState } from 'react';
import axios from 'axios';
import { API_URL } from '../constants';
import { toast } from 'react-toastify';
import { FormGenerator } from '../helper/FormGenerator';
import { xorEncrypt } from '../utils/xorCrypto';

function VendorResetPasswordForm() {
  const [formData, setFormData] = useState({
    email: '',
    otp: Array(6).fill(''),
    password: '',
    confirm_password: '',
  });
  const [step, setStep] = useState('email');
  const [resendCount, setResendCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isPasswordValid, setIsPasswordValid] = useState(true);
  const [isPasswordMatch, setIsPasswordMatch] = useState(true);
  const [sessionToken, setSessionToken] = useState("");

  const handleOtpChange = (index, value) => {
    if (/^\d?$/.test(value)) {
      const newOtp = [...formData.otp];
      newOtp[index] = value;
      setFormData({ ...formData, otp: newOtp });
      if (value && index < 5) {
        document.getElementById(`otp-${index + 1}`)?.focus();
      }
    }
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/vendor/reset-password/init`, {
        email: formData.email,
      });
      toast.success(res.data.message || 'OTP sent!');
      setStep('otp');
      setResendCount((prev) => prev + 1);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error sending OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const otp = formData.otp.join('');
    if (otp.length !== 6) {
      toast.error('Please enter the 6-digit OTP');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/vendor/reset-password/verify`, {
        email: formData.email,
        otp,
      });
      toast.success(res.data.message || 'OTP Verified!');
      setSessionToken(res.data.session_token)
      setStep('password');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error verifying OTP');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!formData.password || formData.password.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }
    if (formData.password !== formData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/vendor/reset-password/update`, {
        email: formData.email,
        password: xorEncrypt(formData.password),
        session_token: sessionToken
      });
      toast.success(res.data.message || 'Password updated!');
      setStep('done');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error updating password');
    } finally {
      setLoading(false);
    }
  };

  const handleGoBackToEmail = () => {
    setStep('email');
    setFormData({ email: '', otp: Array(6).fill(''), password: '', confirm_password: '' });
    setResendCount(0);
  };

  // Password Validation
  const validatePassword = (pwd) => {
    // Minimum 8 characters, alphanumeric (letters and numbers)
    const passwordRegex = /^(?=.*[a-zA-Z])(?=.*\d)[A-Za-z\d]{8,}$/;
    return passwordRegex.test(pwd);
  };

  // Handle password change
  const handlePasswordChange = (e) => {
    const pwd = e.target.value;
    setPassword(pwd);
    setIsPasswordValid(validatePassword(pwd));
    setIsPasswordMatch(pwd === confirmPassword);
  };

  // Handle confirm password change
  const handleConfirmPasswordChange = (e) => {
    const confirmPwd = e.target.value;
    setConfirmPassword(confirmPwd);
    setIsPasswordMatch(confirmPwd === password);
  };

  return (
    <div className="p-4">
      <h4 className="mb-3">Reset Vendor Password</h4>

      {step === 'email' && (
        <>
          <form onSubmit={handleSendOtp}>
            <div className="mb-3">
              <label className="form-label">Email*</label>
              <input
                className="form-control"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required />
            </div>
            <button className="btn btn-primary w-100" type="submit" disabled={loading}>
              {loading ? 'Sending...' : 'Send OTP'}
            </button>
          </form>
          <span className='mt-3'>
            <a href="/vendor/login">Login</a>
          </span>
        </>
      )}

      {step === 'otp' && (
        <>
          <div className="mb-3">
            <label>Enter 6-digit OTP</label>
            <div className="d-flex gap-2 justify-content-between">
              {formData.otp.map((digit, index) => (
                <input
                  key={index}
                  id={`otp-${index}`}
                  type="text"
                  maxLength={1}
                  className="form-control text-center"
                  style={{ width: '40px' }}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                />
              ))}
            </div>
          </div>
          <form onSubmit={handleVerifyOtp}>
            <button className="btn btn-success w-100" type="submit" disabled={loading}>
              {loading ? 'Verifying...' : 'Verify OTP'}
            </button>
          </form>
          <button
            className="btn btn-link mt-2"
            onClick={handleSendOtp}
            disabled={resendCount >= 3 || loading}
          >
            Resend OTP ({3 - resendCount} remaining)
          </button>
          <button className="btn btn-link text-danger" onClick={handleGoBackToEmail}>
            Change Email
          </button>
        </>
      )}

      {step === 'password' && (
        <form onSubmit={handlePasswordSubmit}>
          <div className='row g-3'>
            <FormGenerator
              fields={[
                {
                  id: 'password',
                  label: 'Password',
                  type: 'password',
                  class: 'col-md-6',
                  required: true,
                  hint: 'Password must be at least 8 characters long and contain both letters and numbers.',
                },
                {
                  id: 'confirm_password',
                  label: 'Confirm Password',
                  type: 'password',
                  class: 'col-md-6',
                  required: true,
                },
              ]}
              fieldData={formData}
              onChange={(field, value) =>
                setFormData((prev) => ({
                  ...prev,
                  [field]: value,
                }))
              }
              onSubmit={false}
              hTure={false}
            />
          </div>
          <button className="btn btn-primary mt-3 w-100" type="submit" disabled={loading}>
            {loading ? 'Updating Password...' : 'Update Password'}
          </button>
        </form>
      )}

      {step === 'done' && (
        <div className="alert alert-success text-center">
          Password updated successfully! You can now login with your new credentials.
        </div>
      )}
    </div>
  );
}

export default VendorResetPasswordForm;
